import { apiService } from "../../../utils/api/api.service";
import { requestWithErrorHandling } from "../../../utils/error/handle.error";
import {
  ResolveSellerIdResponse,
  SellerProfileData,
  SellerProfileReportPayload,
  SellerReviewUser,
} from "../interfaces/seller.interface";

class SellerService {
  private static instance: SellerService;

  static getInstance(): SellerService {
    if (!SellerService.instance) {
      SellerService.instance = new SellerService();
    }
    return SellerService.instance;
  }

  /**
   * Resolve a seller slug to a user id. Call when URL param is not a UUID.
   */
  resolveSellerId = (slug: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<ResolveSellerIdResponse>(
        `/products/user/slug/${encodeURIComponent(slug)}`
      );
    });
  };

  /**
   * Fetch seller profile by user id.
   */
  getSellerProfile = (userId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<{ success: boolean; data?: SellerProfileData }>(
        `/products/user/${userId}/profile`
      );
    });
  };

  /**
   * Fetch seller's products by user id. Response: { success, data: { products?: Product[] } | Product[] }
   */
  getSellerProducts = (userId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<{
        success: boolean;
        data?: { products?: unknown[] } | unknown[];
      }>(`/products/user/${userId}`);
    });
  };

  /**
   * Fetch seller reviews by user id. Response: { success, data: { reviews?, averageRating?, totalReviews?, ratingDistribution? } }
   */
  getSellerReviews = (userId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<{
        success: boolean;
        data?: {
          reviews?: Array<{
            id: string;
            rating: number;
            comment: string | null;
            createdAt: string;
            productId?: string;
            productTitle?: string;
            reviewer?: SellerReviewUser;
            user?: SellerReviewUser;
            helpfulYesCount?: number;
            helpfulNoCount?: number;
          }>;
          averageRating?: number;
          totalReviews?: number;
          ratingDistribution?: { [rating: number]: number };
        };
        pagination?: {
          total?: number;
        };
        metadata?: {
          averageRating?: number;
          totalReviews?: number;
          ratingDistribution?: { [rating: number]: number };
        };
      }>(`/products/user/${userId}/reviews`);
    }).then((response) => {
      if (Array.isArray(response?.data) && response?.success) {
        return {
          success: true,
          data: {
            reviews: response.data,
            averageRating: response.metadata?.averageRating ?? 0,
            totalReviews: response.metadata?.totalReviews ?? response.pagination?.total ?? response.data.length,
            ratingDistribution: response.metadata?.ratingDistribution ?? {},
          },
        };
      }
      return response;
    });
  };

  /**
   * Post a review for a seller (user review).
   */
  postSellerReview = (
    userId: string,
    payload: { rating: number; comment: string }
  ) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<{ success: boolean; data?: unknown }>(
        `/products/user/${userId}/reviews`,
        payload
      );
    });
  };

  /**
   * Submit helpfulness vote for a review.
   */
  submitReviewHelpfulness = (reviewId: string, isHelpful: boolean) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<{
        success: boolean;
        data?: { helpfulYesCount?: number; helpfulNoCount?: number };
      }>(`/products/reviews/${reviewId}/helpfulness`, { isHelpful });
    });
  };

  /**
   * Report a seller profile.
   */
  reportSellerProfile = (userId: string, payload: SellerProfileReportPayload) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<{ success: boolean; data?: unknown }>(
        `/products/user/${userId}/reports`,
        payload,
      );
    });
  };
}

export const sellerService = SellerService.getInstance();
